/*
 * Levendi — shop surfaces behaviour (product page + collection grid).
 * Built 6 Sep 2026, mission "audit-build" (worker levendi-build-product).
 *
 * Three independent modules, each fail-open by construction: if one throws or
 * never loads, the page it belongs to still works exactly as it did before.
 *
 *   1. share      — PDP share icon: Web Share API, copy-link fallback.
 *   2. sticky     — mobile PDP action bar, shown once the in-page action area
 *                   scrolls out of view, hidden while the enquiry sheet is open.
 *   3. loadMore   — collection LOAD MORE: fetches ?page=N and appends the
 *                   cards. The numbered pagination stays in the markup as the
 *                   no-JS fallback and is only hidden once this has wired up.
 *
 * Nothing here touches the enquiry sheet's own mechanism. The sticky bar's
 * primary is either the real .levendi-enquire-card trigger (so the existing
 * delegated listener and the enquiry_open event fire untouched) or a proxy
 * that clicks the page's own add-to-cart button.
 */
(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* ---------------------------------------------------------------- share */
  function initShare() {
    var wraps = document.querySelectorAll('[data-lv-share]');
    if (!wraps.length) return;

    Array.prototype.forEach.call(wraps, function (btn) {
      if (btn.dataset.lvShareOn === '1') return;
      btn.dataset.lvShareOn = '1';

      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-share-url') || window.location.href;
        var title = btn.getAttribute('data-share-title') || document.title;

        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {
            /* the visitor dismissed the sheet — nothing to report */
          });
          return;
        }

        var said = function () {
          var wrap = btn.parentNode;
          if (!wrap || !wrap.classList.contains('lv-quiet__sharewrap')) return;
          wrap.classList.add('is-said');
          window.setTimeout(function () {
            wrap.classList.remove('is-said');
          }, 1800);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(said, function () {});
          return;
        }

        try {
          var ta = document.createElement('textarea');
          ta.value = url;
          ta.setAttribute('readonly', '');
          ta.style.position = 'absolute';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          said();
        } catch (e) {}
      });
    });
  }

  /* --------------------------------------------------------------- sticky */
  function initSticky() {
    var bar = document.querySelector('[data-lv-sticky]');
    if (!bar) return;
    if (bar.dataset.lvStickyOn === '1') return;
    bar.dataset.lvStickyOn = '1';

    /* The sticky primary on a purchasable piece proxies the page's own
       add-to-cart button, so the real product form — variant id, quantity
       rules, cart events — stays the single source of truth. */
    var proxy = bar.querySelector('[data-lv-sticky-add]');
    if (proxy) {
      var REAL_SEL =
        '.product-form-buttons .add-to-cart-button, product-form-component .add-to-cart-button';
      /* Scope the lookup to the product-information section. The same selector
         also matches the quick-add buttons in the recommendations rail further
         down the page; document order happens to put the real one first today,
         but that is an accident of layout, not a contract. */
      var findReal = function () {
        var anchor = document.querySelector('[data-lv-actions-anchor]');
        var host = anchor && anchor.closest ? anchor.closest('.shopify-section') : null;
        return (host && host.querySelector(REAL_SEL)) || document.querySelector(REAL_SEL);
      };

      proxy.addEventListener('click', function () {
        var real = findReal();
        if (real && !real.disabled) real.click();
      });

      /* QB-02 (7 Sep 2026): the bar must never say something different from the
         button it proxies. The in-page primary carries Shopify's dynamic label
         ("Add to cart", "Pre-order", "Sold out", "Unavailable") and its own
         disabled state; both are read off that button and mirrored here on
         load and on every variant change. The label is server-rendered too, so
         this only ever corrects — it never introduces a flash. */
      var mirror = function () {
        var real = findReal();
        if (!real) return;
        var src = real.querySelector('.add-to-cart-text__content') || real;
        var label = (src.textContent || '').replace(/\s+/g, ' ').trim();
        if (label && label !== (proxy.textContent || '').trim()) {
          proxy.textContent = label;
        }
        var off = real.disabled === true || real.hasAttribute('disabled');
        if (proxy.disabled !== off) proxy.disabled = off;
        proxy.setAttribute('aria-disabled', off ? 'true' : 'false');
      };

      mirror();

      /* The theme replaces the buy-buttons markup wholesale on a variant
         change, so a listener bound to the old button would be thrown away.
         Listen on document for the theme's own events, and watch the section
         for the swap itself — belt and braces, one repaint apart. */
      var queued = false;
      var schedule = function () {
        if (queued) return;
        queued = true;
        (window.requestAnimationFrame || window.setTimeout)(function () {
          queued = false;
          mirror();
        }, 0);
      };

      document.addEventListener('variant:update', schedule);
      document.addEventListener('variant:change', schedule);
      document.addEventListener('variant:selected', schedule);

      var firstReal = findReal();
      var host = firstReal && firstReal.closest ? firstReal.closest('.shopify-section') : null;
      if (host && window.MutationObserver) {
        new MutationObserver(schedule).observe(host, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['disabled'],
        });
      }
    }

    var anchor = document.querySelector('[data-lv-actions-anchor]');
    if (!anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          /* Show only once the action area has left upward — never while the
             visitor is still above it, at the top of the page. */
          var above = e.boundingClientRect.top < 0;
          if (!e.isIntersecting && above) bar.classList.add('is-on');
          else bar.classList.remove('is-on');
        }
      },
      { threshold: 0, rootMargin: '0px' }
    );
    observer.observe(anchor);

    /* Stand down while the enquiry sheet is open — two stacked action layers
       is exactly the noise this build is removing. */
    var sheet = document.getElementById('levendi-collection-enquiry');
    if (sheet && window.MutationObserver) {
      var sync = function () {
        bar.classList.toggle('is-hidden', sheet.hasAttribute('open'));
      };
      new MutationObserver(sync).observe(sheet, {
        attributes: true,
        attributeFilter: ['open'],
      });
      sync();
    }
  }

  /* ------------------------------------------------------------- loadMore */
  function initLoadMore() {
    var box = document.querySelector('[data-lv-more]');
    if (!box) return;
    if (box.dataset.lvMoreOn === '1') return;
    box.dataset.lvMoreOn = '1';

    var btn = box.querySelector('.lv-more__btn');
    var grid = document.querySelector('.product-grid');
    if (!btn || !grid) return;

    var last = parseInt(grid.getAttribute('data-last-page') || '1', 10) || 1;
    var total = parseInt(box.getAttribute('data-total') || '0', 10) || 0;
    var page = parseInt(box.getAttribute('data-page') || '1', 10) || 1;
    /* The count belongs on the left of the utility bar. Collections whose
       header does not carry one (High Jewellery, Mens) fall back to the count
       printed beside the button; when both exist the fallback stands down so
       the number is never on screen twice. */
    var bar = document.querySelector('.lv-ubar__count[data-lv-count]');
    var fallback = box.querySelector('[data-lv-count]');
    if (bar && fallback) fallback.hidden = true;
    var counts = bar ? [bar] : fallback ? [fallback] : [];
    var busy = false;

    /* Only now is the numbered pagination redundant. Until this line runs the
       fallback is live, which is what a visitor without JS keeps. */
    box.classList.add('is-wired');
    document.documentElement.classList.add('lv-paginated');

    function shown() {
      /* final-qa-linen 18 Sep 2026: the ER editorial break is a
         .product-grid__item too, so it was counted as a piece and the bar read
         "Showing 25 of 60" against 24 cards. Count products, not grid cells. */
      return grid.querySelectorAll('.product-grid__item:not(.lv-gridbreak)').length;
    }

    function say() {
      if (total) {
        var line = 'Showing ' + shown() + ' of ' + total;
        for (var i = 0; i < counts.length; i++) counts[i].textContent = line;
      }
      var left = total ? total - shown() : 0;
      if (page >= last || left <= 0) {
        box.style.display = 'none';
      }
    }

    btn.addEventListener('click', function () {
      if (busy || page >= last) return;
      busy = true;
      btn.disabled = true;
      var label = btn.textContent;
      btn.textContent = 'Loading';

      var url = new URL(window.location.href);
      url.searchParams.set('page', String(page + 1));

      fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('page ' + (page + 1) + ' ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var next = doc.querySelectorAll('.product-grid .product-grid__item');
          if (!next.length) throw new Error('no cards');

          var frag = document.createDocumentFragment();
          Array.prototype.forEach.call(next, function (li) {
            if (li.id && document.getElementById(li.id)) return;
            frag.appendChild(document.importNode(li, true));
          });
          grid.appendChild(frag);

          page += 1;
          box.setAttribute('data-page', String(page));

          /* Keep the crawlable next link honest as the page grows. */
          var relNext = document.querySelector('link[rel="next"]');
          if (relNext) {
            if (page >= last) relNext.remove();
            else {
              var n = new URL(window.location.href);
              n.searchParams.set('page', String(page + 1));
              relNext.setAttribute('href', n.pathname + n.search);
            }
          }

          btn.textContent = label;
          btn.disabled = false;
          busy = false;
          say();
        })
        .catch(function () {
          /* Hand the visitor back a real link rather than a dead button. */
          btn.textContent = label;
          btn.disabled = false;
          busy = false;
          box.classList.add('is-failed');
          document.documentElement.classList.remove('lv-paginated');
        });
    });

    say();
  }

  function boot() {
    try {
      initShare();
    } catch (e) {}
    try {
      initSticky();
    } catch (e) {}
    try {
      initLoadMore();
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();

/* ==========================================================================
   NOW #5 (18 Sep 2026) — the 4-up density choice.
   Three columns are the house default beside the sidebar. A fourth is a
   CHOICE, offered only at 1440 and above where the tile still reads, and
   remembered for the session so a visitor is not asked twice. It drives the
   theme's own `product-grid-view` attribute, so no new grid mechanism enters
   the theme — assets/levendi-finding.css maps `zoom-out` to four columns.
   ========================================================================== */
(function () {
  'use strict';
  function boot() {
    var grid = document.querySelector('.product-grid.product-grid--grid');
    if (!grid) return;
    var bar = document.querySelector('.lv-ubar__in') || document.querySelector('.facets__form-wrapper');
    if (!bar || bar.querySelector('[data-lv-dens]')) return;

    var THREE = '<svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1"><rect x=".5" y=".5" width="4" height="11"/><rect x="5.9" y=".5" width="4" height="11"/><rect x="11.3" y=".5" width="4" height="11"/></svg>';
    var FOUR = '<svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1"><rect x=".5" y=".5" width="3" height="11"/><rect x="4.5" y=".5" width="3" height="11"/><rect x="8.5" y=".5" width="3" height="11"/><rect x="12.5" y=".5" width="3" height="11"/></svg>';

    var wrap = document.createElement('div');
    wrap.className = 'lv-dens';
    wrap.setAttribute('data-lv-dens', '');
    wrap.innerHTML =
      '<button type="button" class="lv-dens__btn" data-lv-dens-v="default" aria-label="Three per row">' + THREE + '</button>' +
      '<button type="button" class="lv-dens__btn" data-lv-dens-v="zoom-out" aria-label="Four per row">' + FOUR + '</button>';
    /* Council Next #23 (18 Sep 2026): the utility bar is a three-slot grid
       (count · chips · sort). The density toggle belongs in the right slot
       beside the sort, not loose in the centre slot where the chips sit. */
    var densHost = bar.querySelector('.lv-ubar__tools');
    if (densHost) densHost.insertBefore(wrap, densHost.firstChild);
    else bar.appendChild(wrap);

    var btns = wrap.querySelectorAll('[data-lv-dens-v]');
    function apply(v, save) {
      if (v === 'zoom-out') grid.setAttribute('product-grid-view', 'zoom-out');
      else grid.removeAttribute('product-grid-view');
      for (var i = 0; i < btns.length; i++) {
        btns[i].setAttribute('aria-pressed', String(btns[i].getAttribute('data-lv-dens-v') === v));
      }
      if (save) { try { sessionStorage.setItem('lv_grid_density', v); } catch (e) {} }
    }
    var saved = 'default';
    try { saved = sessionStorage.getItem('lv_grid_density') || 'default'; } catch (e) {}
    apply(saved, false);

    wrap.addEventListener('click', function (e) {
      var b = e.target.closest('[data-lv-dens-v]');
      if (!b) return;
      apply(b.getAttribute('data-lv-dens-v'), true);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
